/**
 * RebillingWizard — Refacturation IA-first (orchestrateur).
 *
 * 3 étapes composées de sous-composants :
 *   - Step1Source        (choix source : upload PDF ou facture DB)
 *   - Step2Client        (client cible + template optionnel)
 *   - Step3ChatPreview   (édition inline + chat NL + create-draft)
 *
 * L'orchestrateur détient tout le state + tous les side-effects (API calls).
 * Les steps sont des composants "dumb" qui reçoivent des props + callbacks.
 */
import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Sparkles, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import Step1Source from "@/components/rebilling/Step1Source";
import Step2Client from "@/components/rebilling/Step2Client";
import Step3ChatPreview from "@/components/rebilling/Step3ChatPreview";
import { recomputeTotals } from "@/components/rebilling/utils";
import { SUGGESTED_WITH_TEMPLATE } from "@/components/rebilling/utils";

export default function RebillingWizard() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const preselectSupplierInvoiceId = params.get("supplier_invoice_id");

  // Étape 1 : source
  const [step, setStep] = useState(1);
  const [sourceMode, setSourceMode] = useState(preselectSupplierInvoiceId ? "existing" : null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const lastUploadedFile = useRef(null);
  const [state, setState] = useState(null);
  const [supplierList, setSupplierList] = useState([]);
  const [supplierSearch, setSupplierSearch] = useState("");
  const [loadingSupplierList, setLoadingSupplierList] = useState(false);
  // Batch async : jobs en cours (single OU batch de N fichiers)
  const [batchJobs, setBatchJobs] = useState([]);   // [{id, filename, status, error, extraction_line_count, extracted_supplier}]
  const pollTimersRef = useRef({});

  // Étape 2 : client + template
  const [selectedClient, setSelectedClient] = useState(null);
  const [creatorOpen, setCreatorOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [applyingTpl, setApplyingTpl] = useState(false);

  // Étape 3 : chat
  const [chatInput, setChatInput] = useState("");
  const [chatMessages, setChatMessages] = useState([]);
  const [chatSending, setChatSending] = useState(false);
  const [creating, setCreating] = useState(false);
  const chatScrollRef = useRef(null);
  // Guard : évite de re-déclencher l'auto-apply template si l'utilisateur
  // revient de Étape 3 → Étape 2 puis re-clique "Suivant".
  const autoAppliedTplRef = useRef(false);

  useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, [chatMessages, chatSending]);

  // Auto-application du template dès la transition Étape 2 → Étape 3.
  // Si un template a été sélectionné en Étape 2 et que le chat est vide,
  // on envoie automatiquement la consigne "Applique intégralement le style
  // du template" pour déclencher `reshape_all_lines` avec la cross-reference
  // matricules SOURCE ↔ TEMPLATE — plus besoin de cliquer un bouton.
  useEffect(() => {
    if (
      step === 3 &&
      state?.template_invoice_id &&
      chatMessages.length === 0 &&
      !chatSending &&
      !autoAppliedTplRef.current
    ) {
      autoAppliedTplRef.current = true;
      // Petit délai pour laisser le composant Step3 se monter
      setTimeout(() => {
        sendChat(SUGGESTED_WITH_TEMPLATE[0].msg);
      }, 300);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, state?.template_invoice_id]);

  // Auto-préselection si supplier_invoice_id présent en query
  useEffect(() => {
    if (preselectSupplierInvoiceId && !state) {
      loadFromSupplierInvoice(preselectSupplierInvoiceId);
    }
    // eslint-disable-next-line
  }, [preselectSupplierInvoiceId]);

  // --- Étape 1 : PDF upload (async + polling) ---
  // Poll un job jusqu'à done|error. Toutes les 1.5s. Cleanup automatique.
  const pollJob = (jobId) => {
    const tick = async () => {
      try {
        const r = await api.get(`/rebilling/jobs/${jobId}`);
        const j = r.data;
        setBatchJobs((prev) => prev.map((p) => p.id === jobId ? { ...p, ...j } : p));
        if (j.status === "done" || j.status === "error") {
          if (pollTimersRef.current[jobId]) {
            clearTimeout(pollTimersRef.current[jobId]);
            delete pollTimersRef.current[jobId];
          }
          if (j.status === "done") {
            toast.success(
              `« ${j.extracted_supplier?.name || j.filename} » — ${j.extraction_line_count} ligne(s) extraite(s)`,
              { duration: 5000 },
            );
          } else {
            toast.error(`Extraction échouée pour ${j.filename} : ${j.error || "erreur inconnue"}`);
          }
          return;
        }
      } catch (e) {
        // Erreur de poll ponctuelle : on continue quand même (job existe côté backend)
        console.warn("[rebilling] poll error", e);
      }
      pollTimersRef.current[jobId] = setTimeout(tick, 1500);
    };
    tick();
  };

  useEffect(() => {
    // Cleanup à l'unmount
    return () => {
      Object.values(pollTimersRef.current).forEach(clearTimeout);
    };
  }, []);

  const handleUpload = async (fileOrFiles) => {
    const files = Array.isArray(fileOrFiles) ? fileOrFiles : (fileOrFiles ? [fileOrFiles] : []);
    if (files.length === 0) return;
    for (const f of files) {
      if (f.size > 10 * 1024 * 1024) {
        toast.error(`${f.name}: trop volumineux (10 Mo max)`);
        return;
      }
    }
    lastUploadedFile.current = files;
    setUploadError("");
    setUploading(true);
    try {
      let created;
      if (files.length === 1) {
        const fd = new FormData();
        fd.append("file", files[0]);
        const r = await api.post("/rebilling/from-upload-async", fd,
          { headers: { "Content-Type": "multipart/form-data" } });
        created = [r.data];
      } else {
        const fd = new FormData();
        files.forEach((f) => fd.append("files", f));
        const r = await api.post("/rebilling/from-upload-batch", fd,
          { headers: { "Content-Type": "multipart/form-data" } });
        created = r.data.jobs || [];
      }
      // Init state batch (dédup si retry)
      const seed = created.map((c, i) => ({
        id: c.job_id || `err-${Date.now()}-${i}`,
        filename: c.filename || files[i]?.name || "invoice.pdf",
        status: c.status || "pending",
        error: c.error || null,
      }));
      setBatchJobs(seed);
      // Lance poll pour ceux qui ont un job_id
      seed.forEach((j) => { if (!j.id.startsWith("err-")) pollJob(j.id); });
      toast.success(
        files.length === 1
          ? "Extraction lancée — patiente sur cet onglet"
          : `${files.length} extractions lancées en parallèle`,
        { duration: 4000 },
      );
    } catch (e) {
      const msg = apiError(e);
      setUploadError(msg);
      toast.error(msg);
    } finally { setUploading(false); }
  };

  // Utilisateur clique sur un job "done" → charge son state et passe à Step 2
  const continueFromJob = (job) => {
    if (job.status !== "done" || !job.state) return;
    setState(job.state);
    setStep(2);
  };

  const retryUpload = () => {
    if (lastUploadedFile.current) handleUpload(lastUploadedFile.current);
  };

  // --- Étape 1 : depuis DB ---
  const loadSupplierList = async (q = "") => {
    setLoadingSupplierList(true);
    try {
      const r = await api.get("/supplier-invoices/list-enriched", {
        params: { limit: 50, search: q || undefined, sort: "invoice_date", dir: "desc" },
      });
      setSupplierList(r.data.rows || r.data.items || []);
    } catch (e) {
      try {
        const r2 = await api.get("/supplier-invoices");
        setSupplierList(r2.data.invoices || []);
      } catch (e2) {
        toast.error(apiError(e2));
      }
    } finally { setLoadingSupplierList(false); }
  };

  useEffect(() => {
    if (sourceMode === "existing" && supplierList.length === 0) {
      loadSupplierList();
    }
    // eslint-disable-next-line
  }, [sourceMode]);

  const loadFromSupplierInvoice = async (sid) => {
    try {
      const r = await api.post(`/rebilling/from-supplier-invoice/${sid}`);
      setState(r.data.state);
      setSourceMode("existing");
      setStep(2);
      toast.success("Facture fournisseur chargée");
    } catch (e) { toast.error(apiError(e)); }
  };

  const onSupplierSearchChange = (q) => {
    setSupplierSearch(q);
    loadSupplierList(q);
  };

  // --- Étape 2 : templates dès qu'un client est sélectionné ---
  useEffect(() => {
    if (selectedClient?.id && step === 2) {
      api.get("/rebilling/templates", { params: { client_id: selectedClient.id, limit: 10 } })
        .then((r) => setTemplates(r.data.templates || []))
        .catch(() => setTemplates([]));
    } else {
      setTemplates([]);
      setSelectedTemplateId(null);
    }
  }, [selectedClient?.id, step]);

  const applyTemplate = async (tplId) => {
    setApplyingTpl(true);
    try {
      const r = await api.post("/rebilling/apply-template", {
        state, template_invoice_id: tplId,
      });
      setState(r.data.state);
      setSelectedTemplateId(tplId);
      const log = r.data.state?._applied_log?.[0];
      if (log) toast.success(log, { duration: 5000 });
    } catch (e) { toast.error(apiError(e)); }
    finally { setApplyingTpl(false); }
  };

  const revertReshape = () => {};
  const revertLineFromReshape = () => {};

  const clearTemplate = () => {
    setSelectedTemplateId(null);
    autoAppliedTplRef.current = false;  // reset : si un nouveau template est choisi ensuite, on ré-applique
    setState((s) => ({
      ...s,
      notes: "",
      payment_terms_days: 30,
      template_invoice_id: null,
      template_invoice_number: null,
      template_margin_percent: null,
    }));
    toast.info("Template retiré");
  };

  // --- Étape 3 : chat ---
  const sendChat = async (msg) => {
    const text = (msg || chatInput).trim();
    if (!text) return;
    setChatMessages((m) => [...m, { role: "user", text }]);
    setChatInput("");
    setChatSending(true);
    try {
      // Timeout 90s explicite : Gemini reshape peut prendre 20-40s + marge
      // de sécurité avant le tunnel Cloudflare (100s max sur POST).
      const r = await api.post("/rebilling/chat", {
        message: text,
        state: { ...state, _client: selectedClient },
      }, { timeout: 90000 });
      setState(r.data.state);
      setChatMessages((m) => [...m, {
        role: "assistant",
        text: r.data.assistant_message,
        log: r.data.actions_log || [],
        count: r.data.actions_count,
        matricule_cross_ref: r.data.matricule_cross_ref || null,
        matricule_matches_count: r.data.matricule_matches_count || 0,
      }]);
    } catch (e) {
      // Sur timeout Cloudflare 520 / ECONNABORTED axios : message plus utile
      const isTimeout = e.code === "ECONNABORTED" ||
                        String(e?.response?.status) === "520" ||
                        String(apiError(e)).toLowerCase().includes("timeout") ||
                        String(apiError(e)).toLowerCase().includes("cloudflare");
      setChatMessages((m) => [...m, {
        role: "assistant",
        text: isTimeout
          ? "⚠ L'IA a mis trop de temps à répondre (timeout Cloudflare). Les lignes n'ont pas été modifiées. Réessaie avec une consigne plus courte ou clique sur « Appliquer » du widget Marge pour agir manuellement."
          : `⚠ Erreur : ${apiError(e)}`,
        error: true,
      }]);
    } finally { setChatSending(false); }
  };

  // --- Étape 3 : édition inline lignes ---
  const updLine = (i, k, v) => {
    setState((s) => {
      const lines = s.lines.map((ln, idx) => {
        if (idx !== i) return ln;
        const nx = { ...ln, [k]: v };
        // Édition manuelle du PU HT → on invalide le snapshot source pour que
        // la prochaine application de marge "Rapide" reparte du PU édité.
        if (k === "unit_price_ht") delete nx._source_unit_price_ht;
        return nx;
      });
      return recomputeTotals({ ...s, lines });
    });
  };
  const addLine = () =>
    setState((s) => recomputeTotals({
      ...s,
      lines: [...(s.lines || []), {
        description: "Nouvelle ligne", quantity: 1, unit_price_ht: 0,
        vat_rate: 20, discount_percent: 0,
      }],
    }));
  const removeLine = (i) =>
    setState((s) => recomputeTotals({ ...s, lines: s.lines.filter((_, idx) => idx !== i) }));

  // Applique une marge % à TOUTES les lignes en 1 clic (widget MarginControl).
  // IMPORTANT : REMPLACE la marge courante (elle ne se cumule PAS).
  // - Snapshot du prix source (`_source_unit_price_ht`) à la 1re application.
  //   Cliquer 10% puis 20% donne bien 20% (et pas 32%).
  // - 6 décimales pour préserver les petits prix (ex 0.0349 → 0.0419 à 20%).
  const applyMarginToAll = (pct) => {
    setState((s) => {
      const factor = 1 + Number(pct) / 100;
      const lines = (s.lines || []).map((ln) => {
        const src = Number.isFinite(ln._source_unit_price_ht)
          ? Number(ln._source_unit_price_ht)
          : Number(ln.unit_price_ht || 0);
        return {
          ...ln,
          _source_unit_price_ht: src,
          unit_price_ht: Number((src * factor).toFixed(6)),
        };
      });
      return recomputeTotals({ ...s, lines, marge_appliquee_pct: +Number(pct).toFixed(2) });
    });
    toast.success(`Marge ${pct}% appliquée sur ${state?.lines?.length || 0} ligne(s)`);
  };

  // Aperçu PDF : appelle /rebilling/preview-pdf, ouvre le blob dans un modal
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewPdf = async () => {
    setPreviewLoading(true);
    try {
      const r = await api.post("/rebilling/preview-pdf",
        { state, client: selectedClient },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(r.data);
      setPreviewUrl(url);
    } catch (e) { toast.error(apiError(e)); }
    finally { setPreviewLoading(false); }
  };
  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  // --- Étape 3 : création brouillon ---
  const createDraft = async () => {
    if (!selectedClient) { toast.error("Client manquant"); return; }
    if (!(state?.lines || []).length) { toast.error("Aucune ligne"); return; }
    setCreating(true);
    try {
      const r = await api.post("/rebilling/create-draft", {
        state,
        client_id: selectedClient.id,
      });
      toast.success("Brouillon créé, tu peux ajuster puis émettre");
      nav(`/app/invoices/${r.data.invoice.id}/edit`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setCreating(false); }
  };

  // --- Metrics affichés dans la barre latérale récap ---
  // marginPct = marge cumulative EXPLICITEMENT appliquée par l'utilisateur
  // (via MarginControl OU chat IA). N'est PAS le ratio brut cur/source qui
  // peut être trompeur : une extraction imparfaite (Canon PDF total 838€ vs
  // lignes extraites 814€) donnerait -2.9% même si aucune marge n'a été
  // appliquée. Cette métrique reste à 0 tant que l'utilisateur ne l'a pas
  // changé, indépendamment du delta d'extraction.
  const marginPct = useMemo(() => {
    if (state?.marge_appliquee_pct !== undefined && state?.marge_appliquee_pct !== null) {
      return Number(state.marge_appliquee_pct).toFixed(1);
    }
    return "0.0";
  }, [state?.marge_appliquee_pct]);

  // Indicateur secondaire : ratio brut vs source_total_ht (utile pour audit,
  // mais pas affiché en principal car peut refléter un delta d'extraction).
  const extractionDeltaPct = useMemo(() => {
    const src = Number(state?.source_total_ht || 0);
    const cur = Number(state?.subtotal_ht || 0);
    if (!src || src === 0) return null;
    return ((cur / src - 1) * 100).toFixed(1);
  }, [state?.source_total_ht, state?.subtotal_ht]);

  // ------------------------------------------------------------------
  return (
    <div className="max-w-7xl space-y-6" data-testid="rebilling-wizard">
      <div className="flex items-center gap-3">
        <Link to="/app/invoices">
          <Button variant="ghost" size="sm" className="text-slate-500">
            <ArrowLeft className="h-4 w-4 mr-1" /> Retour Factures
          </Button>
        </Link>
      </div>

      <div>
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
            <Sparkles className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900"
                style={{ fontFamily: "Manrope, sans-serif" }}>
              Refacturer un fournisseur
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              L'IA lit ta facture fournisseur, tu ajoutes ta marge en langage naturel,
              on émet la facture client — <b>en 3 clics</b>.
            </p>
          </div>
        </div>
      </div>

      {/* Steps indicator */}
      <div className="flex items-center gap-2">
        {[
          { n: 1, label: "Source fournisseur" },
          { n: 2, label: "Client & template" },
          { n: 3, label: "Ajuster & créer" },
        ].map((s, i, arr) => (
          <div key={s.n} className="flex items-center gap-2 flex-1">
            <div className={cn(
              "h-8 w-8 rounded-full flex items-center justify-center text-xs font-semibold",
              step === s.n ? "bg-violet-600 text-white" :
              step > s.n ? "bg-emerald-500 text-white" :
              "bg-slate-200 text-slate-500"
            )} data-testid={`rebilling-step-${s.n}`}>
              {step > s.n ? <CheckCircle2 className="h-4 w-4" /> : s.n}
            </div>
            <div className={cn(
              "text-xs font-medium",
              step === s.n ? "text-slate-900" : "text-slate-500"
            )}>{s.label}</div>
            {i < arr.length - 1 && <div className="flex-1 h-px bg-slate-200" />}
          </div>
        ))}
      </div>

      {step === 1 && (
        <Step1Source
          sourceMode={sourceMode}
          setSourceMode={setSourceMode}
          uploading={uploading}
          uploadError={uploadError}
          onRetryUpload={retryUpload}
          onUpload={handleUpload}
          batchJobs={batchJobs}
          onContinueJob={continueFromJob}
          supplierList={supplierList}
          supplierSearch={supplierSearch}
          onSearchChange={onSupplierSearchChange}
          loadingSupplierList={loadingSupplierList}
          onSupplierPick={loadFromSupplierInvoice}
        />
      )}

      {step === 2 && state && (
        <Step2Client
          state={state}
          selectedClient={selectedClient}
          setSelectedClient={setSelectedClient}
          templates={templates}
          selectedTemplateId={selectedTemplateId}
          applyingTpl={applyingTpl}
          onApplyTemplate={applyTemplate}
          onClearTemplate={clearTemplate}
          creatorOpen={creatorOpen}
          setCreatorOpen={setCreatorOpen}
          onClientCreated={(c) => { setSelectedClient(c); setCreatorOpen(false); }}
          onBack={() => setStep(1)}
          onContinue={() => setStep(3)}
        />
      )}

      {step === 3 && state && (
        <Step3ChatPreview
          state={state}
          setState={setState}
          marginPct={marginPct}
          extractionDeltaPct={extractionDeltaPct}
          onUpdateLine={updLine}
          onAddLine={addLine}
          onRemoveLine={removeLine}
          onApplyMargin={applyMarginToAll}
          onPreviewPdf={previewPdf}
          selectedClient={selectedClient}
          chatMessages={chatMessages}
          chatSending={chatSending}
          chatInput={chatInput}
          setChatInput={setChatInput}
          onSendChat={sendChat}
          chatScrollRef={chatScrollRef}
          creating={creating}
          onCreateDraft={createDraft}
          onBack={() => setStep(2)}
        />
      )}

      {/* Modal aperçu PDF live du state en cours d'édition */}
      {previewUrl && (
        <div className="fixed inset-0 bg-slate-900/70 z-50 flex items-center justify-center p-4"
             onClick={closePreview}
             data-testid="rebilling-preview-modal">
          <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full h-[90vh] flex flex-col overflow-hidden"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-3 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-violet-600" />
                <span className="font-semibold text-slate-900">Aperçu facture — brouillon</span>
              </div>
              <Button variant="ghost" size="sm" onClick={closePreview}>Fermer</Button>
            </div>
            <object data={previewUrl} type="application/pdf" className="flex-1 w-full">
              <div className="p-8 text-center text-slate-500">
                Aperçu PDF non supporté par ce navigateur.
                <a href={previewUrl} download="apercu.pdf" className="ml-1 text-violet-600 underline">
                  Télécharger le fichier
                </a>
              </div>
            </object>
          </div>
        </div>
      )}
      {previewLoading && (
        <div className="fixed bottom-4 right-4 bg-violet-600 text-white px-4 py-2 rounded-lg shadow-lg text-sm flex items-center gap-2 z-50">
          <Loader2 className="h-4 w-4 animate-spin" /> Génération de l'aperçu…
        </div>
      )}
    </div>
  );
}
